import React, { FormEvent, useState, useEffect } from 'react';
import _ from 'underscore';
import queryString from 'query-string';
import { css } from '@emotion/react';
import { Select, Option, Size } from '@leafygreen-ui/select';
import Banner from '@leafygreen-ui/banner';
import { Link } from '@leafygreen-ui/typography';
import { palette } from '@leafygreen-ui/palette';

// images
import clusterImg from './images/cluster.svg';

// utils
import deepClone from 'js/project/common/util/deepClone';
import { CloudTeams, sendError } from 'js/common/utils/bugsnag';
import clusterTrackingUtil from 'js/project/nds/clusters/util/clusterTrackingUtil';
import analytics, { SEGMENT_EVENTS } from 'js/project/common/util/segmentAnalytics';

// types
import { RegionNames, Region } from 'js/project/nds/types/region';
import { Providers, CloudProvider } from 'js/project/nds/types/provider';
import { DefaultTemplates } from 'js/project/nds/types/defaultTemplate';
import { ReplicationSpec } from 'js/project/nds/types/replicationSpec';
import { Search } from 'js/auth/types/search';
import { VercelProject } from 'js/common/types/vercel';

// api
import clusterFormApi from 'js/common/services/api/nds/clusterFormApi';
import clusterDescriptionsApi from 'js/common/services/api/nds/clusterDescriptionsApi';
import organizationApi from 'js/common/services/api/organizationApi';

// styles
import { VercelDescription, VercelBodyLabel, VercelLabel } from 'js/common/components/Vercel/styles/common';

// context
import { useRequestParams } from 'js/common/context/RequestParamsContext';
import { useAppUserParams } from 'js/auth/context/AppUserContext';

// components
import VercelPage from 'js/common/components/Vercel/VercelPage';
import VercelLayout from 'js/common/components/Vercel/VercelLayout';
import VercelProjectsToClusterLinker from 'js/common/components/Vercel/VercelProjectsToClusterLinker';

const spacedFormSectionStyle = css`
  margin-bottom: 24px;
  &:last-of-type {
    margin-bottom: 40px;
  }
`;

const clusterDescriptionStyle = css`
  font-weight: bold;
  margin-top: 4px;
`;

const clusterDescriptionSectionStyle = css`
  margin-top: 8px;
`;

const clusterDescriptionImgStyle = css`
  float: left;
  margin-right: 16px;
  margin-top: 12px;
`;

interface Props {
  location?: Pick<Location, 'search'>;
  windowLocation?: Pick<Location, 'assign'>;
  clusterName?: string;
}

export default function VercelClusterCreationPage({
  location: { search } = { search: '' },
  windowLocation = window.location,
  clusterName = '',
}: Props) {
  const [providerOptions, setProviderOptions] = useState<Providers | {}>({});
  const [defaultTemplates, setDefaultTemplates] = useState<DefaultTemplates | {}>({});
  const [selectedRegionKey, setSelectedRegionKey] = useState('US_EAST_1');
  const [selectedVercelProjects, setSelectedVercelProjects] = useState<Array<VercelProject>>([]);
  const [hasError, setHasError] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const { groupId, vercelUserId, orgId, next } = queryString.parse(search) as Search;
  const { centralUrl } = useRequestParams();
  const { loadAppUserParams, appUserParams } = useAppUserParams();
  const uniqueClusterName = clusterName || 'Cluster0';

  useEffect(() => {
    loadAppUserParams();
  }, []);

  useEffect(() => {
    async function populateProviderData(): Promise<void> {
      if (typeof groupId !== 'string') {
        return;
      }

      try {
        const getProviderOptions: Promise<Providers> = clusterFormApi.getProviderOptions(groupId, centralUrl);
        const getDefaultTemplates: Promise<
          Pick<DefaultTemplates, CloudProvider.AWS | CloudProvider.GCP | CloudProvider.AZURE>
        > = clusterFormApi.getDefaultTemplates(groupId, centralUrl);

        const [options, defaultTemplates] = await Promise.all([getProviderOptions, getDefaultTemplates]);
        setProviderOptions(options);
        setDefaultTemplates(defaultTemplates);
      } catch (error) {
        sendError({ error, team: CloudTeams.CoreIam });
      }
    }

    populateProviderData();
  }, [groupId, setProviderOptions, setDefaultTemplates]);

  const generateDisplayName = ({ provider, location, name }) => {
    return `${provider} / ${location} (${name})`;
  };

  const generateNewClusterDescription = () => {
    // From the selectedRegion, get the region information and use it to select the provider template to generate the clusterDescription
    const selectedRegionData: Region | undefined = _.find(providerOptions[CloudProvider.FREE].regions, (region) => {
      return region.key === selectedRegionKey;
    });

    const selectedCloudProvider = selectedRegionData?.provider;
    const selectedProviderTemplates = defaultTemplates[selectedCloudProvider as string];
    // default values for 'm0' cluster for the selected provider
    const originalClusterDescription = selectedProviderTemplates['m0'];

    const existingReplicationSpec: ReplicationSpec = originalClusterDescription.replicationSpecList[0];
    const newReplicationSpec: ReplicationSpec = deepClone(existingReplicationSpec);

    // updates the name of the region in originalClusterDescription to the value that is currently selected
    newReplicationSpec.regionConfigs[0].regionName = RegionNames[selectedRegionKey];
    newReplicationSpec.regionConfigs[0].regionView = deepClone(selectedRegionData);

    const clusterDescriptionToBeSent = deepClone(originalClusterDescription);
    clusterDescriptionToBeSent['replicationSpecList'] = [newReplicationSpec];
    return clusterDescriptionToBeSent;
  };

  const redirectToVercel = () => {
    const vercelURI = decodeURI(next as string);

    if (new URL(vercelURI).hostname === 'vercel.com') {
      windowLocation.assign(vercelURI);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsSubmitDisabled(true);

    const clusterDescriptionJson = generateNewClusterDescription();
    if (orgId && vercelUserId && next && groupId) {
      try {
        const { name: clusterName } = await clusterDescriptionsApi.createVercelCluster({
          centralUrl,
          groupId,
          clusterDescriptionJson,
        });

        await organizationApi.linkClusterToVercelProjects({
          centralUrl,
          orgId,
          vercelUserId,
          cluster: clusterName,
          vercelProjects: selectedVercelProjects,
          groupId,
        });

        const analyticsTrackProperties = {
          context: 'Vercel',
          region_autoselect: selectedRegionKey,
          user_country_code: appUserParams.country,
        };
        const basicClusterEventProperties = clusterTrackingUtil.getBasicClusterEventProperties({
          clusterDescription: clusterDescriptionJson,
        });

        analytics.track(SEGMENT_EVENTS.CLUSTER_SUBMITTED_SUCCESSFULLY, {
          ...basicClusterEventProperties,
          ...analyticsTrackProperties,
        });
        redirectToVercel();
      } catch (error) {
        setHasError(true);
        sendError({ error, team: CloudTeams.CoreIam });
      }
    }
  };

  return (
    <VercelPage pageTitle="Vercel Integration">
      <VercelLayout
        title="Create a New Cluster with Vercel"
        isForm
        formProps={{
          onSubmit: handleSubmit,
          buttonText: 'Create New Cluster and Return to Vercel',
          disabled: !(selectedRegionKey && selectedVercelProjects.length > 0) || isSubmitDisabled,
        }}
      >
        {hasError && (
          <Banner variant="danger" css={css({ marginBottom: '24px' })}>
            The integration is set up successfully. However, an error happened with either creating a cluster or
            attempting to link the Atlas cluster to Vercel projects. Please visit the{' '}
            <Link href={`${centralUrl}/v2#/org/${orgId}/integrations`}>Organization&apos;s Integrations</Link> page to
            try again. Click <Link onClick={redirectToVercel}>here</Link> to close this window and return to Vercel.
          </Banner>
        )}
        <div css={spacedFormSectionStyle}>
          <VercelLabel htmlFor="database" id="database_label">
            Database Location
          </VercelLabel>
          <VercelDescription>
            Locate your database as close to your{' '}
            <a href="https://vercel.com/docs/serverless-functions/regions" target="_blank" rel="noreferrer">
              Vercel Serverless Function
            </a>{' '}
            region as possible. If you use Vercel&apos;s default, we recommend AWS / N. Virginia (us-east-1).
          </VercelDescription>
          <Select
            name="database"
            size={Size.Default}
            value={selectedRegionKey}
            aria-labelledby="database_label"
            onChange={(option) => setSelectedRegionKey(option)}
          >
            {providerOptions[CloudProvider.FREE] &&
              providerOptions[CloudProvider.FREE].regions.map(({ provider, location, name, key }) => {
                return (
                  <Option value={key} key={key}>
                    {generateDisplayName({ provider, location, name })}
                  </Option>
                );
              })}
          </Select>
        </div>
        <div css={spacedFormSectionStyle}>
          <VercelBodyLabel>Your Cluster</VercelBodyLabel>
          <VercelDescription>
            You will be using our M0 Sandbox cluster. You can easily scale this cluster at any time in the future by{' '}
            <a href="https://docs.atlas.mongodb.com/scale-cluster/" target="_blank" rel="noreferrer">
              modifying the cluster
            </a>
            .
          </VercelDescription>
          <div css={clusterDescriptionSectionStyle}>
            <img src={clusterImg} alt="cloud" css={clusterDescriptionImgStyle} />
            <VercelBodyLabel>M0 Sandbox</VercelBodyLabel>
            <VercelDescription css={clusterDescriptionStyle}>(Shared RAM, 512 MB storage)</VercelDescription>
          </div>
        </div>
        <div css={spacedFormSectionStyle}>
          <VercelProjectsToClusterLinker
            orgId={orgId}
            centralUrl={centralUrl}
            clusterNames={[uniqueClusterName]}
            defaultClusterName={uniqueClusterName}
            onChangeVercelProjects={setSelectedVercelProjects}
            isClusterDisabled
          />
        </div>
        <VercelDescription css={{ color: palette.gray.dark3 }}>
          A confirmation email will be sent to you with more detailed information regarding your new Atlas project,
          Atlas Cluster, and Vercel integration.
        </VercelDescription>
      </VercelLayout>
    </VercelPage>
  );
}
